import type { MenuItemControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/controls/Menu';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import loc from 'owa-localize';
import Strings from 'owa-locstrings/lib/strings/ribbon_mail_buttons_and_tabs.locstring.json';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { protectionStore } from 'owa-mail-protection/lib/store/protectionStore';
import {
    ENCRYPT_DNF_GUID,
    ENCRYPT_DNRA_GUID,
    ENCRYPT_GUID,
    IRM_DEFAULT_GUID,
} from 'owa-mail-protection/lib/store/schema/rms/EncryptionGUID';
import isCLPEnabled from 'owa-mail-protection/lib/utils/clp/isCLPEnabled';
import { getControlInfo } from 'owa-mail-ribbon-utils';
import { MailRibbonControlId } from 'owa-ribbon-ids';
import type MessageClassificationType from 'owa-service/lib/contract/MessageClassificationType';
import type RightsManagementLicenseDataType from 'owa-service/lib/contract/RightsManagementLicenseDataType';
import {
    onClearMsgPermissionsClicked,
    onToggleMessageClassificationClicked,
    onToggleRmsTemplateClicked,
} from '../../util/commandActions';
import {
    createMailAppToggleButton,
    createMailGeneratedMenuItem,
} from '../../util/createMailRibbonButton';

const isClassificationSelected = (
    messageClassification: MessageClassificationType,
    classificationId: string
): boolean => {
    return messageClassification?.Id == classificationId;
};

const isNoTemplateSelected = (IRMData: RightsManagementLicenseDataType | undefined): boolean => {
    return !IRMData?.RmsTemplateId;
};

const isTemplateSelected = (
    IRMData: RightsManagementLicenseDataType | undefined,
    templateId: string
): boolean => {
    return IRMData?.RmsTemplateId == templateId;
};

export function getEncryptSingleButton(
    keytipPrefixSequence: string[],
    popoutComposeViewState: ComposeViewState | undefined,
    targetWindow: Window
): RibbonControlProps {
    const viewState: ComposeViewState | undefined =
        popoutComposeViewState ?? getComposeViewState()!;
    const { protectionViewState } = viewState;
    const { IRMData } = protectionViewState;
    const controlInfo = getControlInfo(MailRibbonControlId.Encrypt_SingleButton);

    return createMailAppToggleButton(
        getComposeRibbonId(MailRibbonControlId.Encrypt_SingleButton, popoutComposeViewState),
        controlInfo.text,
        controlInfo.tooltip,
        popoutComposeViewState != undefined /*isPopout*/,
        () => {
            onToggleRmsTemplateClicked(
                popoutComposeViewState ?? getComposeViewState()!,
                IRM_DEFAULT_GUID,
                targetWindow
            );
        },
        isTemplateSelected(IRMData, IRM_DEFAULT_GUID),
        constructKeytip(
            keytipPrefixSequence,
            getKeytipString(MailRibbonControlId.Encrypt_SingleButton)
        ),
        controlInfo.iconName
    );
}

export default function getProtectEncryptSections(
    popoutComposeViewState: ComposeViewState | undefined,
    targetWindow: Window
): MenuItemControlProps[] {
    const controls: MenuItemControlProps[] = [];

    if (!isCLPEnabled()) {
        return controls;
    }

    const viewState: ComposeViewState | undefined = popoutComposeViewState ?? getComposeViewState();

    if (viewState === undefined) {
        return controls;
    }

    const { protectionViewState } = viewState;
    const { classificationViewState, IRMData } = protectionViewState;
    const reducedTemplateSetGUIDs: string[] = [ENCRYPT_GUID, ENCRYPT_DNF_GUID, ENCRYPT_DNRA_GUID];
    const { rmsTemplates, clpLabels, messageClassifications } = protectionStore;
    const { messageClassification } = classificationViewState;

    messageClassifications.forEach(classification => {
        const { Id, Name } = classification;

        if (Id) {
            controls.push(
                createMailGeneratedMenuItem(
                    getComposeRibbonId('msgCls_' + Id, popoutComposeViewState),
                    Name!,
                    undefined /*tooltip*/,
                    () => {
                        onToggleMessageClassificationClicked(
                            popoutComposeViewState ?? getComposeViewState()!,
                            classification
                        );
                    },
                    undefined /* keytipProps */,
                    isClassificationSelected(messageClassification, Id)
                )
            );
        }
    });

    const displayAllRmsTemplates: boolean = clpLabels?.length == 0;

    rmsTemplates.forEach(template => {
        const { RmsTemplateId, TemplateName } = template;

        if (
            RmsTemplateId &&
            (displayAllRmsTemplates || reducedTemplateSetGUIDs.indexOf(RmsTemplateId) != -1)
        ) {
            const controlProps: MenuItemControlProps = createMailGeneratedMenuItem(
                getComposeRibbonId('rmsTemplate_' + RmsTemplateId, popoutComposeViewState),
                TemplateName!,
                undefined /*tooltip*/,
                () => {
                    onToggleRmsTemplateClicked(
                        popoutComposeViewState ?? getComposeViewState()!,
                        RmsTemplateId,
                        targetWindow
                    );
                },
                undefined /* keytipProps */,
                isTemplateSelected(IRMData, RmsTemplateId)
            );

            // When displaying reduced set, "Encrypt" comes first
            if (displayAllRmsTemplates || RmsTemplateId != ENCRYPT_GUID) {
                controls.push(controlProps);
            } else {
                controls.unshift(controlProps);
            }
        }
    });

    controls.push(
        createMailGeneratedMenuItem(
            getComposeRibbonId('rmsTemplate_None', popoutComposeViewState),
            loc(Strings.encrypt_clearPermissionsButton),
            undefined /*tooltip*/,
            () => {
                onClearMsgPermissionsClicked(popoutComposeViewState ?? getComposeViewState()!);
            },
            undefined /* keytipProps */,
            isNoTemplateSelected(IRMData)
        )
    );

    return controls;
}
